import { MeResponseSchema, type MeResponse } from '@taqon/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, getJson } from '../api/client';

/**
 * The signed-in session (SDD §22.3). `/api/v1/me` answers 401 when there is none, which is the
 * only signal the SPA uses: an authentication error is never turned into a signed-in state.
 *
 * `capabilities` are permission keys of the form `recordType:action`, used **only** to hide what
 * the user cannot do. The API remains the enforcement point (SDD §22.4), and the SPA never reasons
 * about role names.
 */
export function useSession(): UseQueryResult<MeResponse | null> {
  return useQuery({
    queryKey: ['session'],
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return await getJson('/api/v1/me', MeResponseSchema);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
}

export function hasCapability(session: MeResponse | null | undefined, capability: string): boolean {
  return session?.capabilities.includes(capability) ?? false;
}
